import { ClientApiError, isLiveActivationRequired, isLiveAlreadyExists } from './api'

export type LiveCreateFailKind =
  | 'service_unavailable'
  | 'already_exists'
  | 'configuration'
  | 'validation'
  | 'generic'

export type LiveCreateFailCopy = {
  kind: LiveCreateFailKind
  title: string
  text: string
}

/** Safe Live-create failure copy — never Bridge/Manager/HTTP/traceback details. */
export function classifyLiveCreateFailure(err: unknown): LiveCreateFailCopy {
  if (isLiveAlreadyExists(err)) {
    return {
      kind: 'already_exists',
      title: 'Live Account Already Exists',
      text: 'You already have an active Live trading account.',
    }
  }

  if (err instanceof ClientApiError) {
    if (err.code === 'LIVE_ACCOUNT_CREATION_DISABLED') {
      return {
        kind: 'configuration',
        title: 'Service Configuration Unavailable',
        text: 'Live account creation is currently unavailable. Please contact support.',
      }
    }
    if (
      err.code === 'LIVE_ACCOUNT_CREATION_UNAVAILABLE' ||
      err.code === 'ACCOUNT_CREATION_DISABLED' ||
      err.status === 503 ||
      err.status === 502 ||
      err.status === 504
    ) {
      return {
        kind: 'service_unavailable',
        title: 'Unable to Create Live Account',
        text: 'The trading service is temporarily unavailable. Please try again in a few minutes.',
      }
    }
    if (err.code === 'VALIDATION_ERROR' || err.status === 400 || err.status === 422) {
      const msg = (err.message || '').trim()
      const safeHint =
        msg &&
        !/bridge|manager|traceback|exception|http|sql/i.test(msg) &&
        msg.length <= 160
          ? msg
          : 'Use 8–32 characters with uppercase, lowercase, a digit, and a symbol (!@#$%^&*-_=+).'
      return {
        kind: 'validation',
        title: 'Unable to Create Live Account',
        text: safeHint,
      }
    }
  }

  if (isLiveActivationRequired(err)) {
    return {
      kind: 'service_unavailable',
      title: 'Unable to Create Live Account',
      text: 'The trading service is temporarily unavailable. Please try again in a few minutes.',
    }
  }

  return {
    kind: 'service_unavailable',
    title: 'Unable to Create Live Account',
    text: 'The trading service is temporarily unavailable. Please try again in a few minutes.',
  }
}
